/**
 * Nutrition Service - API client for nutrition-related endpoints
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Calculate nutrition plan (no login required)
 */
export const calculateNutritionPlan = async (inputs) => {
  const response = await fetch(`${API_URL}/nutrition/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inputs)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.errors?.join(', ') || error.error || 'Calculation failed');
  }
  
  return response.json();
};

/**
 * Get protein food references
 */
export const getProteinFoods = async () => {
  const response = await fetch(`${API_URL}/nutrition/protein-foods`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch protein foods');
  }
  
  return response.json();
};

/**
 * Get all nutrition goals for the current user
 */
export const getNutritionGoals = async () => {
  const response = await fetch(`${API_URL}/nutrition/goals`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch goals');
  }
  
  return response.json();
};

/**
 * Get the active nutrition goal
 */
export const getActiveGoal = async () => {
  const response = await fetch(`${API_URL}/nutrition/goals/active`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch active goal');
  }
  
  // API now returns null directly if no active goal
  return response.json();
};

/**
 * Create a new nutrition goal
 */
export const createNutritionGoal = async (goalData) => {
  const response = await fetch(`${API_URL}/nutrition/goals`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(goalData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.errors?.join(', ') || error.error || 'Failed to create goal');
  }
  
  return response.json();
};

/**
 * Get a specific nutrition goal
 */
export const getNutritionGoal = async (goalId) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch goal');
  }
  
  return response.json();
};

/**
 * Update a nutrition goal
 */
export const updateNutritionGoal = async (goalId, updates) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.errors?.join(', ') || error.error || 'Failed to update goal');
  }
  
  return response.json();
};

/**
 * Delete a nutrition goal
 */
export const deleteNutritionGoal = async (goalId) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete goal');
  }
  
  return response.json();
};

/**
 * Update macro overrides for a goal (coach +/- adjustments per client)
 */
export const updateMacroOverrides = async (goalId, overrides) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}/macro-overrides`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(overrides)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update macro overrides');
  }
  
  return response.json();
};

/**
 * Generate share link for a goal
 */
export const shareNutritionGoal = async (goalId) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}/share`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate share link');
  }
  
  return response.json();
};

/**
 * Disable sharing for a goal
 */
export const disableNutritionGoalSharing = async (goalId) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}/share`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to disable sharing');
  }
  
  return response.json();
};

/**
 * Get a shared nutrition plan (public)
 */
export const getSharedNutritionPlan = async (token) => {
  const response = await fetch(`${API_URL}/nutrition/shared/${token}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch shared plan');
  }
  
  return response.json();
};

/**
 * Get meal suggestions for a goal
 */
export const getMealSuggestions = async (goalId) => {
  const response = await fetch(`${API_URL}/nutrition/goals/${goalId}/meals`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch meal suggestions');
  }
  
  return response.json();
};

/**
 * Get high protein menus
 */
export const getHighProteinMenus = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  if (options.minProtein) params.append('minProtein', options.minProtein);
  
  const response = await fetch(`${API_URL}/nutrition/high-protein-menus?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch high protein menus');
  }
  
  return response.json();
};

// Coach endpoints

/**
 * Get all clients for a coach
 */
export const getCoachClients = async (status = null) => {
  const params = status ? `?status=${status}` : '';
  const response = await fetch(`${API_URL}/coach/clients${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch clients');
  }
  
  return response.json();
};

/**
 * Add a new client
 */
export const addClient = async (clientData) => {
  const response = await fetch(`${API_URL}/coach/clients`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(clientData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add client');
  }
  
  return response.json();
};

/**
 * Get a specific client
 */
export const getClient = async (clientId) => {
  const response = await fetch(`${API_URL}/coach/clients/${clientId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch client');
  }
  
  return response.json();
};

/**
 * Update a client
 */
export const updateClient = async (clientId, updates) => {
  const response = await fetch(`${API_URL}/coach/clients/${clientId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update client');
  }
  
  return response.json();
};

/**
 * Delete a client
 */
export const deleteClient = async (clientId) => {
  const response = await fetch(`${API_URL}/coach/clients/${clientId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete client');
  }
  
  return response.json();
};

/**
 * Create nutrition plan for a client
 */
export const createClientNutritionPlan = async (clientId, planData) => {
  const response = await fetch(`${API_URL}/coach/clients/${clientId}/nutrition-plan`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(planData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.errors?.join(', ') || error.error || 'Failed to create plan');
  }
  
  return response.json();
};

/**
 * Get coach statistics
 */
export const getCoachStats = async () => {
  const response = await fetch(`${API_URL}/coach/stats`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch stats');
  }
  
  return response.json();
};

/**
 * Get coaches for current user (as client)
 */
export const getMyCoaches = async () => {
  const response = await fetch(`${API_URL}/coach/my-coaches`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch coaches');
  }
  
  return response.json();
};

/**
 * End coach–client relationship (fitness/nutrition). Removes coach-assigned workouts from your schedule.
 */
export const unsubscribeFromCoach = async (coachClientId) => {
  const response = await fetch(`${API_URL}/coach/unsubscribe/${coachClientId}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to unsubscribe from coach');
  }

  return data;
};

/**
 * Get the nutrition plan assigned to the current user as a client
 * This is used when a client has been assigned a plan by their coach
 */
export const getClientPlan = async () => {
  const response = await fetch(`${API_URL}/nutrition/client-plan`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch client plan');
  }
  
  // API returns null if no client plan exists
  return response.json();
};

const nutritionService = {
  calculateNutritionPlan,
  getProteinFoods,
  getNutritionGoals,
  getActiveGoal,
  createNutritionGoal,
  getNutritionGoal,
  updateNutritionGoal,
  updateMacroOverrides,
  deleteNutritionGoal,
  shareNutritionGoal,
  disableNutritionGoalSharing,
  getSharedNutritionPlan,
  getMealSuggestions,
  getHighProteinMenus,
  getCoachClients,
  addClient,
  getClient,
  updateClient,
  deleteClient,
  createClientNutritionPlan,
  getCoachStats,
  getMyCoaches,
  unsubscribeFromCoach,
  getClientPlan
};

export default nutritionService;
